import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  useCallback,
} from "react";
import {
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Select,
  MenuItem,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import { decimalHoursToHHMM, hhmmToDecimalHours } from "../../utils/dateUtils";
import MyDatePicker from "../../components/MyDatePicker";

const initialChargesState = {
  trip_charges_kms_used: "0",
  trip_charges_slab_km: "0",
  trip_charges_extra_kms: "0",
  trip_charges_days_used: "0.00",
  trip_charges_hours_used: "0:00",
  trip_charges_slab_hour: "0.00",
  trip_charges_slab_rate: "0.00",
  trip_charges_slab_os_rate: "0.00",
  trip_charges_charge_amount: "0.00",
  trip_charges_extra_hour: "0.00",
  trip_charges_slab_extra_hour_rate: "0.00",
  trip_charges_extra_hour_amount: "0.00",
  trip_charges_slab_extra_km_rate: "0.00",
  trip_charges_extra_km_amount: "0.00",
  trip_charges_fuel_esculation_rate: "0.00",
  trip_charges_fuel_esculation_amount: "0.00",
  trip_charges_local_batta: "0.00",
  trip_charges_no_of_os_batta: "0.00",
  trip_charges_os_batta_per_day: "0.00",
  trip_charges_os_batta_total_amount: "0.00",
  trip_charges_no_of_os_night_batta: "0.00",
  trip_charges_os_night_batta: "0.00",
  trip_charges_os_night_batta_total_amount: "0.00",
  trip_charges_permit: "0.00",
  trip_charges_parking: "0.00",
  trip_charges_toll_fasttag: "0.00",
  trip_charges_others: "0.00",
  trip_charges_escort_amount: "0.00",
  trip_charges_advance: "0.00",
  trip_charges_booking_advance: "0.00",
  trip_charges_hire_amount: "0.00",
  trip_charges_fuel_esculation_yes_no: "",
  trip_charges_escort_yes_no: "",
  trip_charges_advance_date: "",
};

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export default function ChargesForm({
  charges,
  setCharges,
  resetSignal,
  dutyForm,
  customerName,
  tariff,
  tariffSlabs,
  tripChargesDaysUsed,
  tripChargesHoursUsed,
  tripChargesKmsUsed,
  hoursUsedDisplay,
  selectedDutyType,
  customerId,
  setTripChargesDaysUsed,
  setTripChargesKmsUsed,
  tripChargesUsed,
  tripHoursUsed,
  tripKmsUsed,
  handleDutyFormChange,
  selectedVehicleGroupId,
  vehicleGroups,
  ref,
  multipleOthersEntry,
  multiplePermitEntry,
  parkingOthersPermitToVehicle,
  usageTimeRoundoff,
}) {
  const [values, setValues] = useState(initialChargesState);

  const emptyChargeEntry = () => ({
    description: "",
    amount: "0.00",
  });

  const [othersEntries, setOthersEntries] = useState([emptyChargeEntry()]);

  const [permitEntries, setPermitEntries] = useState([emptyChargeEntry()]);

  const [othersDialogOpen, setOthersDialogOpen] = useState(false);
  const [permitDialogOpen, setPermitDialogOpen] = useState(false);

  const [othersDialogEntries, setOthersDialogEntries] = useState([]);
  const [permitDialogEntries, setPermitDialogEntries] = useState([]);

  const othersFirstInputRef = useRef(null);
  const permitFirstInputRef = useRef(null);

  const othersInitializedRef = useRef(false);
  const permitInitializedRef = useRef(false);

  const [isEditing, setIsEditing] = useState(false);
  const [manuallyEditedFields, setManuallyEditedFields] = useState(new Set());
  const tariffAppliedRef = useRef(false);
  const manualEditResetTimer = useRef(null);

  // Allow parent to get current values
  useImperativeHandle(ref, () => ({
    getValues: () => ({
      ...values,
      others_entries: multipleOthersEntry ? othersEntries : undefined,
      permit_entries: multiplePermitEntry ? permitEntries : undefined,
    }),
  }));

  const selectedVehicleGroupName =
    vehicleGroups?.find(
      (vg) => String(vg.categoryid) === String(selectedVehicleGroupId),
    )?.categoryname || "";

  // Load saved charges only in edit mode.
  // New charges are initialized by the tariff effect below.
  useEffect(() => {
    if (charges && Object.keys(charges).length > 0) {
      setValues((prev) => ({
        ...prev,
        ...charges,
      }));

      setIsEditing(true);

      const timer = setTimeout(() => {
        setIsEditing(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [charges]);

  useEffect(() => {
    if (!multipleOthersEntry) {
      othersInitializedRef.current = false;
      return;
    }

    if (othersInitializedRef.current) return;

    const existingEntries = Array.isArray(charges?.others_entries)
      ? charges.others_entries
      : [];

    if (existingEntries.length > 0) {
      setOthersEntries(
        existingEntries.map((entry) => ({
          description: entry.description || "",
          amount: parseFloat(entry.amount || 0).toFixed(2),
        })),
      );
    } else {
      const existingTotal = parseFloat(
        charges?.trip_charges_others ?? values.trip_charges_others ?? 0,
      );

      setOthersEntries([
        {
          description: "",
          amount: !isNaN(existingTotal) ? existingTotal.toFixed(2) : "0.00",
        },
      ]);
    }

    othersInitializedRef.current = true;
  }, [multipleOthersEntry, charges, values.trip_charges_others]);

  useEffect(() => {
    if (!multiplePermitEntry) {
      permitInitializedRef.current = false;
      return;
    }

    if (permitInitializedRef.current) return;

    const existingEntries = Array.isArray(charges?.permit_entries)
      ? charges.permit_entries
      : [];

    if (existingEntries.length > 0) {
      setPermitEntries(
        existingEntries.map((entry) => ({
          description: entry.description || "",
          amount: parseFloat(entry.amount || 0).toFixed(2),
        })),
      );
    } else {
      const existingTotal = parseFloat(
        charges?.trip_charges_permit ?? values.trip_charges_permit ?? 0,
      );

      setPermitEntries([
        {
          description: "",
          amount: !isNaN(existingTotal) ? existingTotal.toFixed(2) : "0.00",
        },
      ]);
    }

    permitInitializedRef.current = true;
  }, [multiplePermitEntry, charges, values.trip_charges_permit]);

  // Reset manual edits when tariff/duty changes
  useEffect(() => {
    setManuallyEditedFields(new Set());
    tariffAppliedRef.current = false;
  }, [tariff, tariffSlabs, selectedDutyType, resetSignal]);

  // Utility: merge preserving manual edits for key fields
  const mergePreserveFields = useCallback((prev, newData) => {
    const merged = { ...prev, ...newData };

    const preferOldIfZero = (field) => {
      if (!newData?.[field] || newData[field] === "0.00") {
        merged[field] = prev[field];
      }
    };

    [
      "trip_charges_local_batta",
      "trip_charges_extra_km_amount",
      "trip_charges_extra_hour_amount",
      "trip_charges_fuel_esculation_rate",
      "trip_charges_fuel_esculation_amount",
      "trip_charges_no_of_os_batta",
      "trip_charges_os_batta_per_day",
      "trip_charges_os_batta_total_amount",
      "trip_charges_no_of_os_night_batta",
      "trip_charges_os_night_batta",
      "trip_charges_os_night_batta_total_amount",
      "trip_charges_permit",
      "trip_charges_parking",
      "trip_charges_toll_fasttag",
      "trip_charges_others",
      "trip_charges_escort_amount",
    ].forEach(preferOldIfZero);

    return merged;
  }, []);

  // Apply tariff values once when tariff/slab is ready.
  // Preserve values already entered by the user.
  useEffect(() => {
    if (charges && Object.keys(charges).length > 0) return;

    if (!tariff || !tariffSlabs?.length) return;

    if (tariffAppliedRef.current) return;

    const slab = tariffSlabs[0];

    const nextValues = {
      ...initialChargesState,

      trip_charges_days_used:
        tripChargesDaysUsed !== undefined
          ? tripChargesDaysUsed.toString()
          : "0.00",

      trip_charges_hours_used:
        tripChargesHoursUsed !== undefined
          ? decimalHoursToHHMM(tripChargesHoursUsed)
          : "0:00",

      trip_charges_kms_used:
        tripChargesKmsUsed !== undefined ? tripChargesKmsUsed.toString() : "0",

      trip_charges_slab_rate: slab?.sale_slab_rate
        ? parseFloat(slab.sale_slab_rate).toFixed(2)
        : "0.00",

      trip_charges_slab_km:
        slab?.kms !== undefined ? parseFloat(slab.kms).toFixed(2) : "0.00",

      trip_charges_slab_hour:
        slab?.hours !== undefined ? parseFloat(slab.hours).toFixed(2) : "0.00",

      trip_charges_slab_extra_km_rate: tariff?.sale_extra_km_rate
        ? parseFloat(tariff.sale_extra_km_rate).toFixed(2)
        : "0.00",

      trip_charges_slab_extra_hour_rate: tariff?.sale_extra_hour_rate
        ? parseFloat(tariff.sale_extra_hour_rate).toFixed(2)
        : "0.00",

      trip_charges_fuel_esculation_rate: tariff?.sale_fuel_escalation_per_km
        ? parseFloat(tariff.sale_fuel_escalation_per_km).toFixed(2)
        : "0.00",

      trip_charges_escort_amount: tariff?.sale_escort_amount
        ? parseFloat(tariff.sale_escort_amount).toFixed(2)
        : "0.00",

      trip_charges_charge_amount: slab?.sale_slab_rate
        ? parseFloat(slab.sale_slab_rate).toFixed(2)
        : "0.00",

      trip_charges_hire_amount: slab?.sale_slab_rate
        ? parseFloat(slab.sale_slab_rate).toFixed(2)
        : "0.00",
    };

    setValues((prev) => ({
      ...nextValues,

      // Preserve manually entered values already present.
      trip_charges_local_batta: prev.trip_charges_local_batta || "0.00",

      trip_charges_parking: prev.trip_charges_parking || "0.00",

      trip_charges_permit: prev.trip_charges_permit || "0.00",

      trip_charges_toll_fasttag: prev.trip_charges_toll_fasttag || "0.00",

      trip_charges_others: prev.trip_charges_others || "0.00",
    }));

    tariffAppliedRef.current = true;
  }, [
    tariff,
    tariffSlabs,
    resetSignal,
    charges,
    tripChargesDaysUsed,
    tripChargesHoursUsed,
    tripChargesKmsUsed,
  ]);

  // Auto calculation of extras (km, hour) when no manual editing and relevant inputs change
  useEffect(() => {
    if (tariffAppliedRef.current && manuallyEditedFields.size > 0) {
      return; // skip to respect manual edits
    }
    const slab = tariffSlabs?.[0] || {};
    const kmSlab = parseFloat(slab.kms || values.trip_charges_slab_km || 0);
    const hrSlab = parseFloat(slab.hours || values.trip_charges_slab_hour || 0);
    const baseRate = parseFloat(
      slab.sale_slab_rate || values.trip_charges_slab_rate || 0,
    );

    setValues((prev) => {
      const usedKms = parseFloat(prev.trip_charges_kms_used || 0);

      const usedHours = parseFloat(
        hhmmToDecimalHours(prev.trip_charges_hours_used || "0:00"),
      );

      const extraKms = Math.max(0, usedKms - kmSlab);
      // const extraHours = Math.max(0, usedHours - hrSlab);
      const extraHours =
        usageTimeRoundoff?.chargeable_minutes != null
          ? usageTimeRoundoff.chargeable_minutes / 60
          : Math.max(0, usedHours - hrSlab);

      const extraKmRate =
        prev.trip_charges_slab_extra_km_rate ||
        tariff?.sale_extra_km_rate ||
        slab.extra_km_rate ||
        0;

      const extraHrRate =
        prev.trip_charges_slab_extra_hour_rate ||
        tariff?.sale_extra_hour_rate ||
        slab.extra_hour_rate ||
        0;

      const extraKmAmount = extraKms * parseFloat(extraKmRate);

      const extraHrAmount = extraHours * parseFloat(extraHrRate);

      const nextValues = {
        ...prev,
        trip_charges_slab_km: kmSlab.toFixed(2),
        trip_charges_slab_hour: hrSlab.toFixed(2),
        trip_charges_slab_rate: baseRate.toFixed(2),
        trip_charges_slab_extra_km_rate: parseFloat(extraKmRate).toFixed(2),
        trip_charges_slab_extra_hour_rate: parseFloat(extraHrRate).toFixed(2),
        trip_charges_extra_kms: extraKms.toFixed(2),
        trip_charges_extra_hour: extraHours.toFixed(2),
        trip_charges_extra_km_amount: extraKmAmount.toFixed(2),
        trip_charges_extra_hour_amount: extraHrAmount.toFixed(2),
      };

      const changed = Object.keys(nextValues).some(
        (key) => nextValues[key] !== prev[key],
      );

      return changed ? nextValues : prev;
    });
  }, [
    tariff,
    tariffSlabs,
    selectedDutyType,
    tripChargesUsed,
    tripHoursUsed,
    tripKmsUsed,
    values.trip_charges_kms_used,
    values.trip_charges_hours_used,
    values.trip_charges_slab_km,
    values.trip_charges_slab_hour,
    values.trip_charges_slab_rate,
    values.trip_charges_slab_extra_km_rate,
    values.trip_charges_slab_extra_hour_rate,
    manuallyEditedFields,
    usageTimeRoundoff,
  ]);

  // Sync usage values from parent when not editing any field manually
  useEffect(() => {
    if (isEditing) return;
    if (
      tripChargesDaysUsed !== undefined &&
      tripChargesHoursUsed !== undefined &&
      tripChargesKmsUsed !== undefined
    ) {
      setValues((prev) => ({
        ...prev,
        trip_charges_days_used: tripChargesDaysUsed.toString(),
        trip_charges_hours_used: decimalHoursToHHMM(tripChargesHoursUsed),
        trip_charges_kms_used: tripChargesKmsUsed.toString(),
      }));
    }
  }, [
    tripChargesDaysUsed,
    tripChargesHoursUsed,
    tripChargesKmsUsed,
    isEditing,
  ]);

  // Update parent total charges on usage or tariff changes
  useEffect(() => {
    if (!tariff) {
      setCharges(0);
      return;
    }
    let total = 0;
    if (tripChargesDaysUsed && tariff.dayRate)
      total += tripChargesDaysUsed * tariff.dayRate;
    if (tripChargesHoursUsed && tariff.hourRate)
      total += tripChargesHoursUsed * tariff.hourRate;
    if (tripChargesKmsUsed && tariff.kmRate)
      total += tripChargesKmsUsed * tariff.kmRate;
    setCharges(total);
  }, [
    tariff,
    tripChargesDaysUsed,
    tripChargesHoursUsed,
    tripChargesKmsUsed,
    setCharges,
  ]);

  useEffect(() => {
    if (!othersDialogOpen) return;

    const timer = setTimeout(() => {
      othersFirstInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [othersDialogOpen]);

  useEffect(() => {
    if (!permitDialogOpen) return;

    const timer = setTimeout(() => {
      permitFirstInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [permitDialogOpen]);

  // Debounced handleChange to smooth rapid updates and edits
  const debouncedSetEditingFalse = useRef(null);
  const handleChange = (field, value) => {
    setIsEditing(true);
    setManuallyEditedFields((prev) => new Set(prev).add(field));
    setValues((prev) => ({ ...prev, [field]: value }));
    handleDutyFormChange("charges", { ...values, [field]: value });

    if (debouncedSetEditingFalse.current)
      clearTimeout(debouncedSetEditingFalse.current);
    debouncedSetEditingFalse.current = setTimeout(
      () => setIsEditing(false),
      800,
    );
  };

  // Optional handleBlur to finalize formatting
  const handleBlur = () => {
    if (debouncedSetEditingFalse.current)
      clearTimeout(debouncedSetEditingFalse.current);
    debouncedSetEditingFalse.current = setTimeout(
      () => setIsEditing(false),
      300,
    );
  };

  const sanitizeAmount = (value) => {
    let cleanedValue = value.replace(/[^0-9.]/g, "");

    const parts = cleanedValue.split(".");

    if (parts.length > 2) {
      cleanedValue = parts[0] + "." + parts.slice(1).join("");
    }

    return cleanedValue;
  };

  const getEntryTotal = (entries) => {
    return entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
  };

  /* ============================================================
   OTHERS
============================================================ */

  const openOthersDialog = () => {
    if (!multipleOthersEntry) return;

    setOthersDialogEntries(
      othersEntries.map((entry) => ({
        description: entry.description || "",
        amount: entry.amount || "0.00",
      })),
    );

    setOthersDialogOpen(true);
  };

  const handleOthersDescriptionChange = (index, value) => {
    setOthersDialogEntries((prev) => {
      const next = [...prev];

      next[index] = {
        ...next[index],
        description: value,
      };

      return next;
    });
  };

  const handleOthersAmountChange = (index, value) => {
    const cleanedValue = sanitizeAmount(value);

    setOthersDialogEntries((prev) => {
      const next = [...prev];

      next[index] = {
        ...next[index],
        amount: cleanedValue,
      };

      return next;
    });
  };

  const addOthersEntry = () => {
    setOthersDialogEntries((prev) => [...prev, emptyChargeEntry()]);
  };

  const removeOthersEntry = (index) => {
    setOthersDialogEntries((prev) => {
      if (prev.length <= 1) {
        return [emptyChargeEntry()];
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  const saveOthersEntries = () => {
    const cleanedEntries = othersDialogEntries.map((entry) => ({
      description: entry.description.trim(),
      amount: parseFloat(entry.amount || 0).toFixed(2),
    }));

    const totalFormatted = getEntryTotal(cleanedEntries).toFixed(2);

    const nextValues = {
      ...values,
      trip_charges_others: totalFormatted,
      others_entries: cleanedEntries,
    };

    console.log("=== OTHERS POPUP SAVE ===");
    console.log("cleanedEntries:", cleanedEntries);
    console.log("totalFormatted:", totalFormatted);
    console.log(
      "nextValues.trip_charges_others:",
      nextValues.trip_charges_others,
    );
    console.log("nextValues:", nextValues);

    setOthersEntries(cleanedEntries);
    setValues(nextValues);

    setIsEditing(true);

    setManuallyEditedFields(
      (prev) => new Set([...prev, "trip_charges_others"]),
    );

    console.log("Calling handleDutyFormChange with:", nextValues);

    handleDutyFormChange("charges", nextValues);

    setOthersDialogOpen(false);

    if (debouncedSetEditingFalse.current) {
      clearTimeout(debouncedSetEditingFalse.current);
    }

    debouncedSetEditingFalse.current = setTimeout(
      () => setIsEditing(false),
      800,
    );
  };

  const closeOthersDialog = () => {
    setOthersDialogOpen(false);
  };

  const renderOthersDialog = () => {
    const total = getEntryTotal(othersDialogEntries);

    return (
      <Dialog
        open={othersDialogOpen}
        onClose={closeOthersDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontSize: 17,
            fontWeight: 700,
            color: "#1e293b",
          }}
        >
          Multiple Others Entry
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            {/* Header */}
            <Grid
              container
              spacing={1}
              sx={{
                mb: 1,
                fontWeight: "bold",
              }}
            >
              <Grid item xs={7}>
                Description
              </Grid>

              <Grid item xs={4}>
                Amount
              </Grid>

              <Grid item xs={1} />
            </Grid>

            {/* Entries */}
            {othersDialogEntries.map((entry, index) => (
              <Grid
                container
                spacing={1}
                alignItems="center"
                key={index}
                sx={{ mb: 1 }}
              >
                <Grid item xs={7}>
                  <TextField
                    fullWidth
                    size="small"
                    value={entry.description}
                    placeholder="Description"
                    inputRef={index === 0 ? othersFirstInputRef : undefined}
                    onChange={(e) =>
                      handleOthersDescriptionChange(index, e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    value={entry.description}
                    placeholder="Description"
                    inputRef={index === 0 ? othersFirstInputRef : undefined}
                    onChange={(e) =>
                      handleOthersDescriptionChange(index, e.target.value)
                    }
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 36,
                        fontSize: 14,
                        color: "#1e293b",
                      },
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#1e293b",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#cbd5e1",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={1}>
                  <IconButton
                    type="button"
                    size="small"
                    onClick={() => removeOthersEntry(index)}
                    disabled={othersDialogEntries.length <= 1}
                    color="error"
                    aria-label="Delete entry"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            {/* Add + Total */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Button type="button" variant="text" onClick={addOthersEntry}>
                + Add Others
              </Button>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1e293b",
                }}
              >
                Total: {total.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button type="button" onClick={closeOthersDialog} color="inherit">
            Cancel
          </Button>

          <Button type="button" onClick={saveOthersEntries} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  /* ============================================================
   PERMIT
============================================================ */

  const openPermitDialog = () => {
    if (!multiplePermitEntry) return;

    setPermitDialogEntries(
      permitEntries.map((entry) => ({
        description: entry.description || "",
        amount: entry.amount || "0.00",
      })),
    );

    setPermitDialogOpen(true);
  };

  const handlePermitDescriptionChange = (index, value) => {
    setPermitDialogEntries((prev) => {
      const next = [...prev];

      next[index] = {
        ...next[index],
        description: value,
      };

      return next;
    });
  };

  const handlePermitAmountChange = (index, value) => {
    const cleanedValue = sanitizeAmount(value);

    setPermitDialogEntries((prev) => {
      const next = [...prev];

      next[index] = {
        ...next[index],
        amount: cleanedValue,
      };

      return next;
    });
  };

  const addPermitEntry = () => {
    setPermitDialogEntries((prev) => [...prev, emptyChargeEntry()]);
  };

  const removePermitEntry = (index) => {
    setPermitDialogEntries((prev) => {
      if (prev.length <= 1) {
        return [emptyChargeEntry()];
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  const savePermitEntries = () => {
    const cleanedEntries = permitDialogEntries.map((entry) => ({
      description: entry.description.trim(),
      amount: parseFloat(entry.amount || 0).toFixed(2),
    }));

    const totalFormatted = getEntryTotal(cleanedEntries).toFixed(2);

    const nextValues = {
      ...values,
      trip_charges_permit: totalFormatted,
      permit_entries: cleanedEntries,
    };

    setPermitEntries(cleanedEntries);
    setValues(nextValues);

    setIsEditing(true);

    setManuallyEditedFields(
      (prev) => new Set([...prev, "trip_charges_permit"]),
    );

    handleDutyFormChange("charges", nextValues);

    setPermitDialogOpen(false);

    if (debouncedSetEditingFalse.current) {
      clearTimeout(debouncedSetEditingFalse.current);
    }

    debouncedSetEditingFalse.current = setTimeout(
      () => setIsEditing(false),
      800,
    );
  };

  const closePermitDialog = () => {
    setPermitDialogOpen(false);
  };

  const renderPermitDialog = () => {
    const total = getEntryTotal(permitDialogEntries);

    return (
      <Dialog
        open={permitDialogOpen}
        onClose={closePermitDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontSize: 17,
            fontWeight: 700,
            color: "#1e293b",
          }}
        >
          Multiple Permit Entry
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            {/* Header */}
            <Grid
              container
              spacing={1}
              sx={{
                mb: 1,
                fontWeight: "bold",
              }}
            >
              <Grid item xs={7}>
                Description
              </Grid>

              <Grid item xs={4}>
                Amount
              </Grid>

              <Grid item xs={1} />
            </Grid>

            {/* Entries */}
            {permitDialogEntries.map((entry, index) => (
              <Grid
                container
                spacing={1}
                alignItems="center"
                key={index}
                sx={{ mb: 1 }}
              >
                <Grid item xs={7}>
                  <TextField
                    fullWidth
                    size="small"
                    value={entry.description}
                    placeholder="Description"
                    inputRef={index === 0 ? permitFirstInputRef : undefined}
                    onChange={(e) =>
                      handlePermitDescriptionChange(index, e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    value={entry.amount}
                    placeholder="0.00"
                    onChange={(e) =>
                      handlePermitAmountChange(index, e.target.value)
                    }
                    inputProps={{
                      style: {
                        textAlign: "right",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={1}>
                  <IconButton
                    size="small"
                    onClick={() => removePermitEntry(index)}
                    disabled={permitDialogEntries.length <= 1}
                  >
                    −
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            {/* Add + Total */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Button variant="text" onClick={addPermitEntry}>
                + Add Permit
              </Button>

              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Total: {total.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closePermitDialog} color="inherit">
            Cancel
          </Button>

          <Button onClick={savePermitEntries} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderField = (fieldName, placeholder) => {
    const isKmQuantityField =
      fieldName.toLowerCase().includes("kms_used") ||
      fieldName.toLowerCase().includes("extra_kms") ||
      fieldName.toLowerCase().endsWith("_km");

    const isHoursField = fieldName === "trip_charges_hours_used";
    const isExtraHourField = fieldName === "trip_charges_extra_hour";

    const getDisplayValue = () => {
      const val = values[fieldName];
      if (val === undefined || val === null || val === "") {
        if (isExtraHourField || isHoursField) return "0:00";
        if (isKmQuantityField) return "0";
        return "0.00";
      }
      if (isExtraHourField) return decimalHoursToHHMM(Number(val));
      if (isHoursField) return val.toString();

      if (isKmQuantityField) {
        const num = parseFloat(val);
        return !isNaN(num) ? Math.floor(num).toString() : val.toString();
      }

      const num = parseFloat(val);
      return !isNaN(num) ? num.toFixed(2) : val.toString();
    };

    const handleChangeInternal = (e) => {
      let val = e.target.value;
      if (!isHoursField && !isExtraHourField) {
        val = val.replace(/[^0-9.]/g, "");
        const parts = val.split(".");
        if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
      } else {
        val = val.replace(/[^0-9:]/g, "");
        const countColons = (val.match(/:/g) || []).length;
        if (countColons > 1) val = val.substring(0, val.lastIndexOf(":"));
      }
      handleChange(fieldName, val);
    };

    const handleBlurInternal = () => {
      if (!isHoursField && !isExtraHourField) {
        const val = parseFloat(values[fieldName]);
        if (!isNaN(val)) {
          handleChange(
            fieldName,
            isKmQuantityField ? Math.floor(val).toString() : val.toFixed(2),
          );
        }
      }
    };

    return (
      <TextField
        size="small"
        variant="outlined"
        placeholder={placeholder}
        value={getDisplayValue()}
        onChange={handleChangeInternal}
        onBlur={() => {
          handleBlurInternal();
          handleBlur();
        }}
        // sx={{ width: "100%", maxWidth: 140, m: 0.5 }}
        sx={commonInputStyle}
        inputProps={{ style: { textAlign: "right" } }}
        type="text"
      />
    );
  };

  // --------------------------------------------------------

  // UseEffect for LOCAL Duty Type
  useEffect(() => {
    // ✅ ADD THIS LINE IMMEDIATELY AFTER THE IF CHECK

    if (selectedDutyType === "Local") {
      if (isEditing) return;

      const slabRate = parseFloat(values.trip_charges_slab_rate || 0);

      // Convert hours used to decimal
      const hoursUsedDecimal = hhmmToDecimalHours(
        values.trip_charges_hours_used || "0:00",
      );
      const kmsUsed = parseFloat(values.trip_charges_kms_used || 0);

      // Slab limits
      const slabHour = parseFloat(values.trip_charges_slab_hour || 0);
      const slabKm = parseFloat(values.trip_charges_slab_km || 0);

      // Extra calculations
      const extraHour =
        usageTimeRoundoff?.chargeable_minutes != null
          ? usageTimeRoundoff.chargeable_minutes / 60
          : Math.max(0, hoursUsedDecimal - slabHour);
      const extraKms = Math.max(0, kmsUsed - slabKm);

      const extraHourAmount =
        extraHour * parseFloat(values.trip_charges_slab_extra_hour_rate || 0);
      const extraKmAmount =
        extraKms * parseFloat(values.trip_charges_slab_extra_km_rate || 0);

      // Fuel escalation
      const fuelEscRate =
        values.trip_charges_fuel_esculation_yes_no === "Yes"
          ? parseFloat(tariff?.sale_fuel_escalation_per_km || 0)
          : 0;
      const fuelEscAmount = fuelEscRate * kmsUsed;

      // Escort
      const escortAmount =
        values.trip_charges_escort_yes_no === "Yes"
          ? parseFloat(tariff?.sale_escort_amount || 0)
          : 0;

      const battaTotal =
        (parseFloat(charges?.trip_charges_local_batta) || 0) +
        (parseFloat(charges?.trip_charges_os_batta_total_amount) || 0) +
        (parseFloat(charges?.trip_charges_os_night_batta_total_amount) || 0);
      // Total hire amount
      const hireAmount =
        slabRate +
        extraKmAmount +
        extraHourAmount +
        fuelEscAmount +
        battaTotal +
        (parseFloat(values.trip_charges_permit) || 0) +
        (parseFloat(values.trip_charges_parking) || 0) +
        (parseFloat(values.trip_charges_toll_fasttag) || 0) +
        (parseFloat(values.trip_charges_others) || 0) +
        escortAmount -
        (parseFloat(values.trip_charges_advance) || 0);

      setValues((prev) => ({
        ...prev,
        trip_charges_charge_amount: slabRate.toFixed(2),
        trip_charges_extra_kms: Math.round(extraKms).toString(), // no decimals
        trip_charges_extra_hour: extraHour.toFixed(2),
        trip_charges_extra_km_amount: extraKmAmount.toFixed(2),
        trip_charges_extra_hour_amount: extraHourAmount.toFixed(2),
        trip_charges_fuel_esculation_rate: fuelEscRate.toFixed(2),
        trip_charges_fuel_esculation_amount: fuelEscAmount.toFixed(2),
        trip_charges_escort_amount: escortAmount.toFixed(2),
        trip_charges_hire_amount: hireAmount.toFixed(2),
      }));
    }
  }, [
    values.trip_charges_slab_rate,
    values.trip_charges_slab_extra_km_rate,
    values.trip_charges_slab_extra_hour_rate,
    values.trip_charges_hours_used,
    values.trip_charges_kms_used,
    values.trip_charges_slab_hour,
    values.trip_charges_slab_km,
    values.trip_charges_permit,
    values.trip_charges_parking,
    values.trip_charges_toll_fasttag,
    values.trip_charges_others,
    values.trip_charges_escort_yes_no,
    values.trip_charges_fuel_esculation_yes_no,
    values.trip_charges_advance,
    tariff,
    isEditing,
    usageTimeRoundoff,
  ]);

  // useEffect for OUTSTATION Duty Type
  useEffect(() => {
    if (selectedDutyType !== "Outstation") return;
    if (isEditing) return;
    const safeParseNonZero = (val, fallback) => {
      const p = parseFloat(val);
      // returns parsed value if falsy or less than 100, else fallback
      return !p || p < 100 ? p : fallback;
    };

    const slabOsRate = safeParseNonZero(
      values.trip_charges_slab_os_rate &&
        values.trip_charges_slab_os_rate !== "0" &&
        values.trip_charges_slab_os_rate !== "0.00"
        ? values.trip_charges_slab_os_rate
        : tariff?.sale_os_km_rate || 0,
      0,
    );

    const minKmPerDay = parseFloat(tariff?.sale_os_min_kms || 0);
    const usedDays = parseFloat(values.trip_charges_days_used || 1);

    const slabKms = minKmPerDay * usedDays;

    const kmsUsed = parseFloat(values.trip_charges_kms_used || 0);

    const chargeKms = Math.max(kmsUsed, slabKms);
    const baseChargeAmount = slabOsRate * chargeKms;

    const safeParse = (val, fallback = 0) => {
      const parsed = parseFloat(val);
      return isNaN(parsed) || parsed === 0 ? fallback : parsed;
    };

    const noOfOsBatta = parseFloat(values.trip_charges_days_used || 1);

    const osBattaPerDay = safeParse(
      values.trip_charges_os_batta_per_day,
      safeParse(tariff?.sale_driver_batta, 0),
    );

    const noOfOsNightBatta = safeParse(
      values.trip_charges_no_of_os_night_batta,
      0,
    );
    const osNightBatta = safeParse(
      values.trip_charges_os_night_batta,
      safeParse(tariff?.sale_night_batta, 0),
    );

    const osBattaTotalAmount = noOfOsBatta * osBattaPerDay;
    const osNightBattaTotalAmount = noOfOsNightBatta * osNightBatta;

    // Fuel escalation calculation added
    const fuelEscRate =
      values.trip_charges_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.sale_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount =
      fuelEscRate * (parseFloat(values.trip_charges_kms_used) || 0);

    // Escort calculation added
    const escortAmount =
      values.trip_charges_escort_yes_no === "Yes"
        ? parseFloat(tariff?.sale_escort_amount || 0)
        : 0;

    const totalHireAmount =
      baseChargeAmount +
      osBattaTotalAmount +
      osNightBattaTotalAmount +
      fuelEscAmount +
      (parseFloat(values.trip_charges_local_batta) || 0) +
      (parseFloat(values.trip_charges_permit) || 0) +
      (parseFloat(values.trip_charges_parking) || 0) +
      (parseFloat(values.trip_charges_toll_fasttag) || 0) +
      (parseFloat(values.trip_charges_others) || 0) +
      escortAmount -
      ((parseFloat(values.trip_charges_advance) || 0) +
        (parseFloat(values.trip_charges_booking_advance) || 0));

    setValues((prev) => ({
      ...prev,
      trip_charges_slab_os_rate: slabOsRate.toString(),
      trip_charges_slab_km: slabKms.toString(),
      trip_charges_charge_amount: baseChargeAmount.toFixed(2),
      trip_charges_no_of_os_batta: noOfOsBatta.toString(),
      trip_charges_os_batta_per_day: osBattaPerDay.toFixed(2),
      trip_charges_os_batta_total_amount: osBattaTotalAmount.toFixed(2),
      trip_charges_no_of_os_night_batta: noOfOsNightBatta.toString(),
      trip_charges_os_night_batta: osNightBatta.toFixed(2),
      trip_charges_os_night_batta_total_amount:
        osNightBattaTotalAmount.toFixed(2),
      trip_charges_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_charges_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_charges_escort_amount: escortAmount.toFixed(2),
      trip_charges_hire_amount: totalHireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    values.trip_charges_slab_os_rate,
    values.trip_charges_slab_km,
    values.trip_charges_no_of_os_batta,
    values.trip_charges_os_batta_per_day,
    values.trip_charges_no_of_os_night_batta,
    values.trip_charges_os_night_batta,
    values.trip_charges_fuel_esculation_yes_no,
    values.trip_charges_escort_yes_no,
    values.trip_charges_fuel_esculation_amount,
    values.trip_charges_local_batta,
    values.trip_charges_permit,
    values.trip_charges_parking,
    values.trip_charges_toll_fasttag,
    values.trip_charges_others,
    values.trip_charges_escort_amount,
    values.trip_charges_advance,
    values.trip_charges_booking_advance,
    values.trip_charges_days_used,
    values.trip_charges_kms_used,
    tariff,
    isEditing,
  ]);

  // -----------------------------

  // UseEffect for AirportTransfer Duty Type
  useEffect(() => {
    if (selectedDutyType !== "AirportTransfer") return;
    if (isEditing) return;
    // Convert hours used to decimal
    const hoursUsedDecimal = hhmmToDecimalHours(
      values.trip_charges_hours_used || "0:00",
    );
    const kmsUsed = parseFloat(values.trip_charges_kms_used || 0);

    // Slab limits
    const slabHour = parseFloat(values.trip_charges_slab_hour || 0);
    const slabKm = parseFloat(values.trip_charges_slab_km || 0);

    // Slab rate editable or fallback to tariff
    const slabRate =
      parseFloat(values.trip_charges_slab_rate) ||
      parseFloat(tariff?.sale_airport_transfer_rate || 0);

    // Extra calculations (strict from usage - slab)
    const extraHour =
      usageTimeRoundoff?.chargeable_minutes != null
        ? usageTimeRoundoff.chargeable_minutes / 60
        : Math.max(0, hoursUsedDecimal - slabHour);
    const extraKms = Math.max(0, kmsUsed - slabKm);

    const extraHourAmount =
      extraHour * parseFloat(values.trip_charges_slab_extra_hour_rate || 0);
    const extraKmAmount =
      extraKms * parseFloat(values.trip_charges_slab_extra_km_rate || 0);

    // Fuel escalation calculation
    const fuelEscRate =
      values.trip_charges_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.sale_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount = fuelEscRate * kmsUsed;

    // Escort calculation
    const escortAmount =
      values.trip_charges_escort_yes_no === "Yes"
        ? parseFloat(tariff?.sale_escort_amount || 0)
        : 0;

    // Charge amount equals slab rate + extra amounts
    const chargeAmount =
      slabRate + extraHourAmount + extraKmAmount + fuelEscAmount + escortAmount;

    // Total hire amount including extras and deductions
    const hireAmount =
      chargeAmount +
      (parseFloat(values.trip_charges_permit) || 0) +
      (parseFloat(values.trip_charges_parking) || 0) +
      (parseFloat(values.trip_charges_toll_fasttag) || 0) +
      (parseFloat(values.trip_charges_others) || 0) -
      (parseFloat(values.trip_charges_advance) || 0);

    setValues((prev) => ({
      ...prev,
      trip_charges_slab_hour:
        values.trip_charges_slab_hour || slabHour.toString(),
      trip_charges_slab_km: values.trip_charges_slab_km || slabKm.toString(),
      trip_charges_extra_hour: extraHour.toFixed(2),
      trip_charges_extra_kms: extraKms.toFixed(2),
      trip_charges_extra_hour_amount: extraHourAmount.toFixed(2),
      trip_charges_extra_km_amount: extraKmAmount.toFixed(2),
      trip_charges_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_charges_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_charges_escort_amount: escortAmount.toFixed(2),
      trip_charges_slab_rate: slabRate.toFixed(2),
      trip_charges_charge_amount: slabRate.toFixed(2),
      trip_charges_hire_amount: hireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    values.trip_charges_hours_used,
    values.trip_charges_kms_used,
    values.trip_charges_slab_hour,
    values.trip_charges_slab_km,
    values.trip_charges_slab_extra_hour_rate,
    values.trip_charges_slab_extra_km_rate,
    values.trip_charges_fuel_esculation_yes_no,
    values.trip_charges_escort_yes_no,
    values.trip_charges_slab_rate,
    values.trip_charges_permit,
    values.trip_charges_parking,
    values.trip_charges_toll_fasttag,
    values.trip_charges_others,
    values.trip_charges_advance,
    tariff,
    isEditing,
    usageTimeRoundoff,
  ]);

  //UseEffect for EmployeeTransfer Duty Type
  useEffect(() => {
    if (selectedDutyType !== "EmployeeTransfer") return;
    if (isEditing) return;
    // Convert hours used to decimal
    const hoursUsedDecimal = hhmmToDecimalHours(
      values.trip_charges_hours_used || "0:00",
    );
    const kmsUsed = parseFloat(values.trip_charges_kms_used || 0);

    // Slab limits
    const slabHour = parseFloat(values.trip_charges_slab_hour || 0);
    const slabKm = parseFloat(values.trip_charges_slab_km || 0);

    // Extra calculations
    const extraHour =
      usageTimeRoundoff?.chargeable_minutes != null
        ? usageTimeRoundoff.chargeable_minutes / 60
        : Math.max(0, hoursUsedDecimal - slabHour);
    const extraKms = Math.max(0, kmsUsed - slabKm);

    const extraHourAmount =
      extraHour * parseFloat(values.trip_charges_slab_extra_hour_rate || 0);
    const extraKmAmount =
      extraKms * parseFloat(values.trip_charges_slab_extra_km_rate || 0);

    // Fuel escalation calculation
    const fuelEscRate =
      values.trip_charges_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.sale_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount = fuelEscRate * kmsUsed;

    // Escort calculation
    const escortAmount =
      values.trip_charges_escort_yes_no === "Yes"
        ? parseFloat(tariff?.sale_escort_amount || 0)
        : 0;

    // Slab rate editable or fallback to employee transfer tariff rate
    const slabRate =
      parseFloat(values.trip_charges_slab_rate) ||
      parseFloat(tariff?.sale_employee_transfer_rate || 0);

    // Charge amount = slab rate + extras
    const chargeAmount =
      slabRate + extraHourAmount + extraKmAmount + fuelEscAmount + escortAmount;

    const battaTotal =
      (parseFloat(charges?.trip_charges_local_batta) || 0) +
      (parseFloat(charges?.trip_charges_os_batta_total_amount) || 0) +
      (parseFloat(charges?.trip_charges_os_night_batta_total_amount) || 0);

    // Total hire amount including extras and deductions
    const hireAmount =
      chargeAmount +
      (parseFloat(values.trip_charges_permit) || 0) +
      (parseFloat(values.trip_charges_parking) || 0) +
      (parseFloat(values.trip_charges_toll_fasttag) || 0) +
      (parseFloat(values.trip_charges_others) || 0) -
      (parseFloat(values.trip_charges_advance) || 0);

    setValues((prev) => ({
      ...prev,
      trip_charges_slab_hour:
        values.trip_charges_slab_hour || slabHour.toString(),
      trip_charges_slab_km: values.trip_charges_slab_km || slabKm.toString(),
      trip_charges_extra_hour: extraHour.toFixed(2),
      trip_charges_extra_kms: extraKms.toFixed(2),
      trip_charges_extra_hour_amount: extraHourAmount.toFixed(2),
      trip_charges_extra_km_amount: extraKmAmount.toFixed(2),
      trip_charges_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_charges_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_charges_escort_amount: escortAmount.toFixed(2),
      trip_charges_slab_rate: slabRate.toFixed(2),
      trip_charges_charge_amount: slabRate.toFixed(2),
      trip_charges_hire_amount: hireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    values.trip_charges_hours_used,
    values.trip_charges_kms_used,
    values.trip_charges_slab_hour,
    values.trip_charges_slab_km,
    values.trip_charges_slab_extra_hour_rate,
    values.trip_charges_slab_extra_km_rate,
    values.trip_charges_fuel_esculation_yes_no,
    values.trip_charges_escort_yes_no,
    values.trip_charges_slab_rate,
    values.trip_charges_permit,
    values.trip_charges_parking,
    values.trip_charges_toll_fasttag,
    values.trip_charges_others,
    values.trip_charges_advance,
    tariff,
    isEditing,
    usageTimeRoundoff,
  ]);

  useEffect(() => {
    if (selectedDutyType !== "Contract") return;
    if (isEditing) return;
    const hoursUsed = values.trip_charges_hours_used || "0:00";
    const kmsUsed = parseFloat(values.trip_charges_kms_used || 0);

    setValues((prev) => ({
      ...prev,
      trip_charges_hours_used: hoursUsed,
      trip_charges_kms_used: kmsUsed.toString(),

      // Clear or hide other calculation fields
      trip_charges_slab_hour: "",
      trip_charges_slab_km: "",
      trip_charges_extra_hour: "",
      trip_charges_extra_kms: "",
      trip_charges_extra_hour_amount: "",
      trip_charges_extra_km_amount: "",
      trip_charges_fuel_esculation_rate: "",
      trip_charges_fuel_esculation_amount: "",
      trip_charges_escort_amount: "",
      trip_charges_slab_rate: "",
      trip_charges_charge_amount: "",
      trip_charges_hire_amount: "",
    }));
  }, [
    selectedDutyType,
    values.trip_charges_hours_used,
    values.trip_charges_kms_used,
    isEditing,
  ]);

  // On values or relevant dependencies change, run calculation
  useEffect(() => {
    if (selectedDutyType !== "OSDayRate") return;
    if (isEditing) return;
    const daysUsed = parseFloat(values.trip_charges_days_used) || 1;
    const kmsUsed = parseFloat(values.trip_charges_kms_used) || 0;

    // const safeParseNonZero = (val, fallback) => {
    //   const p = parseFloat(val);
    //   return !p ? fallback : p;
    // };

    const safeParseNonZero = (val, fallback) => {
      const p = parseFloat(val);
      return !p || p < 100 ? fallback : p;
    };

    const slabOsRate = safeParseNonZero(
      values.trip_charges_slab_os_rate,
      parseFloat(tariff?.sale_os_day_rate) || 0,
    );

    console.log(
      "OS day rate ",
      values.trip_charges_slab_os_rate,
      tariff?.sale_os_day_rate,
    );

    const minKmsPerDay = parseFloat(tariff?.sale_os_min_kms ?? 0);
    const slabKms = minKmsPerDay * daysUsed;

    const slabExtraKmRate = parseFloat(tariff?.sale_os_km_rate ?? 0);
    const baseChargeAmount = slabOsRate * daysUsed;

    const extraKms = Math.max(0, kmsUsed - slabKms);

    const extraKmAmount = extraKms * slabExtraKmRate;

    const noOfOsBatta = parseFloat(values.trip_charges_days_used) || 1;
    const osBattaPerDay = safeParseNonZero(
      values.trip_charges_os_batta_per_day,
      safeParseNonZero(tariff?.sale_driver_batta, 0),
    );

    const noOfOsNightBatta = safeParseNonZero(
      values.trip_charges_no_of_os_night_batta,
      0,
    );
    const osNightBatta = safeParseNonZero(
      values.trip_charges_os_night_batta,
      safeParseNonZero(tariff?.sale_night_batta, 0),
    );

    const osBattaTotalAmount = noOfOsBatta * osBattaPerDay;
    const osNightBattaTotalAmount = noOfOsNightBatta * osNightBatta;

    const fuelEscRate =
      values.trip_charges_fuel_esculation_yes_no === "Yes"
        ? parseFloat(tariff?.sale_fuel_escalation_per_km || 0)
        : 0;
    const fuelEscAmount = fuelEscRate * kmsUsed;

    const escortAmount =
      values.trip_charges_escort_yes_no === "Yes"
        ? parseFloat(tariff?.sale_escort_amount || 0)
        : 0;

    const totalHireAmount =
      baseChargeAmount +
      extraKmAmount +
      osBattaTotalAmount +
      osNightBattaTotalAmount +
      fuelEscAmount +
      (parseFloat(values.trip_charges_local_batta) || 0) +
      (parseFloat(values.trip_charges_permit) || 0) +
      (parseFloat(values.trip_charges_parking) || 0) +
      (parseFloat(values.trip_charges_toll_fasttag) || 0) +
      (parseFloat(values.trip_charges_others) || 0) +
      escortAmount -
      ((parseFloat(values.trip_charges_advance) || 0) +
        (parseFloat(values.trip_charges_booking_advance) || 0));

    setValues((prev) => ({
      ...prev,
      trip_charges_slab_os_rate: slabOsRate.toFixed(2),
      trip_charges_slab_extra_km_rate: slabExtraKmRate.toString(),
      trip_charges_slab_km: slabKms.toString(),
      trip_charges_extra_kms: Math.floor(extraKms).toString(),
      trip_charges_extra_km_amount: extraKmAmount.toFixed(2),
      trip_charges_charge_amount: baseChargeAmount.toFixed(2),
      trip_charges_no_of_os_batta: noOfOsBatta.toString(),
      trip_charges_os_batta_per_day: osBattaPerDay.toFixed(2),
      trip_charges_os_batta_total_amount: osBattaTotalAmount.toFixed(2),
      trip_charges_no_of_os_night_batta: noOfOsNightBatta.toString(),
      trip_charges_os_night_batta: osNightBatta.toFixed(2),
      trip_charges_os_night_batta_total_amount:
        osNightBattaTotalAmount.toFixed(2),
      trip_charges_fuel_esculation_rate: fuelEscRate.toFixed(2),
      trip_charges_fuel_esculation_amount: fuelEscAmount.toFixed(2),
      trip_charges_escort_amount: escortAmount.toFixed(2),
      trip_charges_hire_amount: totalHireAmount.toFixed(2),
    }));
  }, [
    selectedDutyType,
    tariff,
    values,
    values.trip_charges_slab_os_rate,
    values.trip_charges_days_used,
    values.trip_charges_kms_used,
    values.trip_charges_no_of_os_batta,
    values.trip_charges_os_batta_per_day,
    values.trip_charges_no_of_os_night_batta,
    values.trip_charges_os_night_batta,
    isEditing,
  ]);

  const inputStyle = {
    fontSize: 14,
    height: 26,
    color: "#1e293b",

    "& .MuiInputBase-input": {
      paddingTop: "2px",
      paddingBottom: "2px",
      fontSize: 14,
      fontWeight: 600,
      color: "#1e293b",
      WebkitTextFillColor: "#1e293b",
      height: 22,
      boxSizing: "border-box",
      textAlign: "right",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },

    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
  };

  const commonInputStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    ...inputStyle,
  };

  const dropdownStyle = {
    width: "100%",
    maxWidth: "100%",
    fontSize: 14,
    fontWeight: 600,
    color: "#1e293b",
    height: 24,
    minWidth: 0,

    "& .MuiSelect-select": {
      paddingTop: 2,
      paddingBottom: 2,
      height: 16,
      display: "flex",
      alignItems: "center",
      boxSizing: "border-box",
      fontSize: 14,
      fontWeight: 600,
      color: "#1e293b",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },

    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
  };

  const noRowHoverStyle = {
    "&:hover": {
      backgroundColor: "transparent",
    },
    "& .MuiInputBase-root:focus, & .MuiInputBase-root.Mui-focused": {
      backgroundColor: "inherit",
    },
  };

  const renderDropdown = (fieldName) => (
    <Select
      size="small"
      displayEmpty
      value={values[fieldName] || ""}
      onChange={(e) => handleChange(fieldName, e.target.value)}
      sx={dropdownStyle}
    >
      <MenuItem value="">
        <em>Select</em>
      </MenuItem>
      <MenuItem value="Yes">Yes</MenuItem>
      <MenuItem value="No">No</MenuItem>
    </Select>
  );

  console.log("ChargesForm internal values:", values);

  return (
    <Grid container spacing={1} padding={1}>
      <Grid item xs={12}>
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            boxSizing: "border-box",
            // paddingRight: "5px", // Right padding
            borderRadius: 8, // rounded corners
            border: "1px solid #ddd", // optional border for visibility
          }}
        >
          {/* <Table size="small" style={{ tableLayout: 'fixed' }}> */}

          <Table
            sx={{
              "& tr": {
                height: 32,
              },
              "& td, & th": {
                paddingTop: 0.2,
                paddingBottom: 0,
                fontSize: 13,
                lineHeight: 1.3,
                color: "#1e293b",
                boxSizing: "border-box",
              },
              "& th": {
                fontWeight: 700,
                color: "#334155",
              },
              "& .MuiTableCell-root": {
                borderColor: "#cbd5e1",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ height: 36 }}>
                <TableCell
                  colSpan={5}
                  sx={{ fontWeight: "bold", fontSize: 16, padding: 1 }}
                >
                  Customer :{" "}
                  <span style={{ color: "#1976d2" }}>
                    {customerName || "(No customer selected)"}
                  </span>
                </TableCell>
              </TableRow>

              <TableRow sx={{ height: 24 }}>
                <TableCell colSpan={5}>
                  <Box
                    sx={{
                      height: 24,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {/* Duty Type aligned left */}
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ flexGrow: 1 }}
                    >
                      <span
                        style={{
                          height: 32,
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          marginRight: 4,
                        }}
                      >
                        Duty Type:
                      </span>
                      <span
                        style={{
                          color: "#1976d2",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          marginRight: 16,
                        }}
                      >
                        {selectedDutyType ? (
                          <a
                            style={{ color: "#1976d2", textDecoration: "none" }}
                          >
                            {selectedDutyType}
                          </a>
                        ) : (
                          "(None)"
                        )}
                      </span>
                    </Box>

                    {/* Vehicle Group aligned right */}
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ justifyContent: "flex-end", minWidth: 160 }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          marginRight: 4,
                        }}
                      >
                        Vehicle Group:
                      </span>
                      <span
                        style={{
                          color: "#1976d2",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                        }}
                      >
                        {selectedVehicleGroupName || "(None)"}
                      </span>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>

              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5", // light gray background
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)", // subtle bottom border
                }}
              >
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Particulars
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Hours/Days
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Kms
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Rate
                </TableCell>
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                    fontWeight: "bold",
                  }}
                >
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* All rows below with the same column width settings */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>Usages</TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {selectedDutyType === "Outstation" ||
                  selectedDutyType === "OSDayRate" ||
                  selectedDutyType === "Package"
                    ? renderField("trip_charges_days_used", "Days Used")
                    : renderField(
                        "trip_charges_hours_used",
                        "Hours Used",
                        hoursUsedDisplay,
                      )}
                </TableCell>

                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_charges_kms_used", "Kms Used")}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
              </TableRow>

              {/* Charges - hide for Contract */}
              {selectedDutyType !== "Contract" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Charges
                  </TableCell>
                  {selectedDutyType === "Outstation" ||
                  selectedDutyType === "OSDayRate" ||
                  selectedDutyType === "EmployeeTransfer" ||
                  selectedDutyType === "Package" ? (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {/* Dummy empty cell to maintain layout */}
                    </TableCell>
                  ) : (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField("trip_charges_slab_hour", "Slab Hour")}
                    </TableCell>
                  )}

                  {selectedDutyType === "EmployeeTransfer" ? (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {/* Empty dummy cell */}
                    </TableCell>
                  ) : (
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField("trip_charges_slab_km", "Slab Km")}
                    </TableCell>
                  )}

                  <TableCell>
                    {["Outstation", "OSDayRate", "Package"].includes(
                      selectedDutyType,
                    )
                      ? renderField("trip_charges_slab_os_rate", "Slab OS Rate")
                      : renderField("trip_charges_slab_rate", "Slab Rate")}
                  </TableCell>

                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_charges_charge_amount", "Charge Amount")}
                  </TableCell>
                </TableRow>
              )}

              {/* Extra Hour and Local Batta rows hidden for OSDayRate */}
              {!(
                selectedDutyType === "Outstation" ||
                selectedDutyType === "OSDayRate" ||
                selectedDutyType === "Package" ||
                selectedDutyType === "EmployeeTransfer" ||
                selectedDutyType === "Contract"
              ) && (
                <>
                  {/* Extra Hour */}
                  <TableRow sx={noRowHoverStyle}>
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      Extra Hour
                    </TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField("trip_charges_extra_hour", "Extra Hour")}
                    </TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100 }} />
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField(
                        "trip_charges_slab_extra_hour_rate",
                        "Slab Extra Hour Rate",
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100 }}>
                      {renderField(
                        "trip_charges_extra_hour_amount",
                        "Extra Hour Amount",
                      )}
                    </TableCell>
                  </TableRow>
                </>
              )}

              {/* Show Extra Kms row only for OSDayRate, Local, and AirportTransfer */}
              {(selectedDutyType === "OSDayRate" ||
                selectedDutyType === "Local" ||
                selectedDutyType === "AirportTransfer") && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Extra Kms
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_charges_extra_kms", "Extra Kms")}
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField(
                      "trip_charges_slab_extra_km_rate",
                      "Slab Extra Km Rate",
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField(
                      "trip_charges_extra_km_amount",
                      "Extra Km Amount",
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* Local Batta - only for Local duty type */}
              {selectedDutyType === "Local" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Local Batta
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_charges_local_batta", "Local Batta")}
                  </TableCell>
                </TableRow>
              )}

              {/* Fuel Esculation */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Fuel Esculation
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderDropdown("trip_charges_fuel_esculation_yes_no")}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField(
                    "trip_charges_fuel_esculation_rate",
                    "Fuel Escalation Rate",
                  )}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField(
                    "trip_charges_fuel_esculation_amount",
                    "Fuel Escalation Amount",
                  )}
                </TableCell>
              </TableRow>

              {selectedDutyType !== "Local" &&
                selectedDutyType !== "AirportTransfer" &&
                selectedDutyType !== "EmployeeTransfer" &&
                selectedDutyType !== "Contract" && (
                  <>
                    {/* OS Day Batta */}
                    <TableRow sx={noRowHoverStyle}>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        OS Day Batta
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_charges_no_of_os_batta",
                          "No of OS Batta",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_charges_os_batta_per_day",
                          "OS Batta per Day",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_charges_os_batta_total_amount",
                          "OS Batta Total Amount",
                        )}
                      </TableCell>
                    </TableRow>

                    {/* OS Night Batta */}
                    <TableRow sx={noRowHoverStyle}>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        OS Night Batta
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_charges_no_of_os_night_batta",
                          "No of OS Night Batta",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_charges_os_night_batta",
                          "OS Night Batta",
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {renderField(
                          "trip_charges_os_night_batta_total_amount",
                          "OS Night Batta Total Amount",
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Permit */}
                    <TableRow sx={noRowHoverStyle}>
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        Permit
                      </TableCell>
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }} />
                      <TableCell sx={{ width: 100, minWidth: 100 }}>
                        {multiplePermitEntry ? (
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            value={(
                              parseFloat(values.trip_charges_permit || 0) || 0
                            ).toFixed(2)}
                            placeholder="Permit"
                            InputProps={{
                              readOnly: true,
                            }}
                            onClick={openPermitDialog}
                            sx={commonInputStyle}
                            inputProps={{
                              style: {
                                textAlign: "right",
                                cursor: "pointer",
                              },
                            }}
                          />
                        ) : (
                          renderField("trip_charges_permit", "Permit")
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              {/* Parking */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Parking
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_charges_parking", "Parking")}
                </TableCell>
              </TableRow>

              {/* Toll/Fasttag */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Toll/Fasttag
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_charges_toll_fasttag", "Toll/Fasttag")}
                </TableCell>
              </TableRow>

              {/* Others */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>Others</TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {multipleOthersEntry ? (
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={(
                        parseFloat(values.trip_charges_others || 0) || 0
                      ).toFixed(2)}
                      placeholder="Others"
                      InputProps={{
                        readOnly: true,
                      }}
                      onClick={openOthersDialog}
                      sx={commonInputStyle}
                      inputProps={{
                        style: {
                          textAlign: "right",
                          cursor: "pointer",
                        },
                      }}
                    />
                  ) : (
                    renderField("trip_charges_others", "Others")
                  )}
                </TableCell>
              </TableRow>

              {/* Escorts */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  Escorts
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderDropdown("trip_charges_escort_yes_no")}
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }}>
                  {renderField("trip_charges_escort_amount", "Escort Amount")}
                </TableCell>
              </TableRow>

              {/* Advance - hide for Contract */}
              {selectedDutyType !== "Contract" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Advance
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 120, minHeight: 28 }}>
                    <TextField
                      size="small"
                      type="date"
                      value={
                        dutyForm.trip_charges_advance_date
                          ? dutyForm.trip_charges_advance_date.split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        handleDutyFormChange(
                          "trip_charges_advance_date",
                          e.target.value
                            ? new Date(e.target.value).toISOString()
                            : null,
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        width: 105,
                        minHeight: 28,
                        "& .MuiInputBase-root": { minHeight: 20 },
                        "& .MuiInputBase-input": {
                          fontSize: "0.80rem",
                          minHeight: 28,
                          padding: "0 4px",
                          lineHeight: 1.2,
                        },
                        "& label": {
                          fontSize: "0.85rem",
                          lineHeight: 1,
                          marginBottom: 0,
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField("trip_charges_advance", "Advance")}
                  </TableCell>
                </TableRow>
              )}

              {/* Booking Advance - hide for Contract */}
              {selectedDutyType !== "Contract" && (
                <TableRow sx={noRowHoverStyle}>
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    Booking Advance
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }} />
                  <TableCell sx={{ width: 100, minWidth: 100 }}>
                    {renderField(
                      "trip_charges_booking_advance",
                      "Booking Advance",
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* Total */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell
                  sx={{ width: 100, minWidth: 100, fontWeight: "bold" }}
                >
                  Total
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell sx={{ width: 100, minWidth: 100 }} />
                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    color: "#1976D2",
                  }}
                >
                  {renderField("trip_charges_hire_amount", "Hire Amount")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Grid>
      {multipleOthersEntry && renderOthersDialog()}
      {multiplePermitEntry && renderPermitDialog()}
    </Grid>
  );
}
